import { action, computed, makeObservable, reaction } from 'mobx';

import { AlertsStore } from '@/configs/store/AlertsStore';
import { LoadingStageModel } from '@/models/LoadingStageModel';
import { LocalStore } from '@/models/LocalStore';
import { ValueModel } from '@/models/ValueModel';
import { trackerApi } from '@/tracker/api/trackerApi';
import { Board } from '@/tracker/types';
import { showError } from '@/tracker/utils/showError';

const PAGE_SIZE = 50;
const SEARCH_DELAY_MS = 300;

/** Каталог досок: поиск по названию на сервере, подгрузка страниц по курсору — как каталог очередей */
export class BoardsStore extends LocalStore {
  private readonly _alertsStore: AlertsStore;
  private readonly _boards = new ValueModel<Board[]>([]);
  private readonly _query = new ValueModel('');
  private readonly _nextPageToken = new ValueModel<string | null>(null);
  private readonly _totalSize = new ValueModel(0);
  private readonly _loadingStage = new LoadingStageModel();
  /** Ответ на устаревший поиск не должен перетереть свежий */
  private _request = 0;

  constructor({ alertsStore }: { alertsStore: AlertsStore }) {
    super();
    this._alertsStore = alertsStore;

    makeObservable(this, {
      boards: computed,
      query: computed,
      totalSize: computed,
      hasMore: computed,
      isLoading: computed,
      isLoaded: computed,
      isError: computed,
      load: action.bound,
      loadMore: action.bound,
      setQuery: action.bound,
    });

    this.addReaction(
      reaction(
        () => this._query.value,
        () => this.load(),
        { delay: SEARCH_DELAY_MS },
      ),
    );
  }

  get boards() {
    return this._boards.value;
  }

  get query() {
    return this._query.value;
  }

  get totalSize() {
    return this._totalSize.value;
  }

  get hasMore() {
    return this._nextPageToken.value !== null;
  }

  get isLoading() {
    return this._loadingStage.isLoading;
  }

  /** Последний запрос успешен — можно показывать «ничего не нашлось» */
  get isLoaded() {
    return this._loadingStage.isSuccess;
  }

  get isError() {
    return this._loadingStage.isError;
  }

  setQuery(query: string) {
    this._query.change(query);
    // Курсор привязан к query: подгрузка со старым до перезагрузки списка дала бы 400
    this._nextPageToken.change(null);
  }

  /** Первая страница под текущий поиск */
  load() {
    return this.loadPage(null);
  }

  loadMore() {
    return this.loadPage(this._nextPageToken.value);
  }

  private async loadPage(pageToken: string | null) {
    const request = ++this._request;

    try {
      this._loadingStage.loading();

      const page = await trackerApi.searchBoards({
        query: this._query.value.trim() || undefined,
        pageSize: PAGE_SIZE,
        pageToken: pageToken ?? undefined,
      });

      if (request !== this._request) return;

      this._boards.change(pageToken ? [...this._boards.value, ...page.items] : page.items);
      this._nextPageToken.change(page.nextPageToken);
      this._totalSize.change(page.totalSize);
      this._loadingStage.success();
    } catch (error) {
      if (request !== this._request) return;

      this._loadingStage.error();
      showError({ alertsStore: this._alertsStore, title: 'Не удалось загрузить доски', error });
    }
  }
}
